import React, { useEffect, useState } from "react";
import DrawerCustom from "../../../../shared/general/Drawer";
import { useTranslation } from "react-i18next";
import { Box, Grid, InputAdornment, styled, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";

import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import TextFieldTheme from "../../../../shared/general/TextFieldTheme";
import ButtonBlue from "../../../../shared/general/ButtonBlue";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import Swal from 'sweetalert2'

import { getEmployeeProfile, getEmployeeSalaryChange, updateEmployeeSalaryChange } from "../../../../../../actions/employee";
import NumberFormatTheme from "../../../../shared/general/NumberFormatTheme";

const StyledRoot = styled(Box)({
  padding: "16px",
  "& .MuiAutocomplete-root": {
    "& .MuiOutlinedInput-root": {
      padding: "13.5px 14px",
      paddingRight: "32px",
      "& input": {
        padding: 0,
      },
    },
  },
});

const DrawerEditSalaryChange = (props) => {

  const { drawerConfig, handleClose } = props;

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const validateYupSchema = yup.object({
    salaryValue: yup.string().nullable().required(`${t("ThisFieldIsRequired")}`),
    start: yup.date().nullable().required(`${t("ThisFieldIsRequired")}`),
  });

  const useHookForm = useForm({
    defaultValues: {
      salaryValue: "",
      start: null,
      end: null,
      remark: "",
    },
    resolver: yupResolver(validateYupSchema),
    mode: "all",
  })

  const onSubmit = async (data) => {

    const formData = {
      salaryValue: data.salaryValue,
      start: dayjs(data.start).format("YYYY-MM-DD"),
      remark: data.remark || null,
    }

    const result = await dispatch(updateEmployeeSalaryChange(drawerConfig.data.idEmp, drawerConfig.data.idEmployeeSalary, formData));

    if (result && result.status === 200) {
      handleClose();
      Swal.fire({
        title: t("DataSaveSuccessful"),
        icon: "success",
        timer: 2000,
        customClass: {
          container: "my-swal-container",
        }
      });
      dispatch(getEmployeeSalaryChange(drawerConfig.data.idEmp));
      dispatch(getEmployeeProfile(drawerConfig.data.idEmp));
    } else if (result && result.data && result.data.message) {
      Swal.fire({
        title: t(`${result.data.message}`),
        icon: "error",
        customClass: {
          container: "my-swal-container",
        }
      });
    } else {
      Swal.fire({
        title: t("AnErrorOccurred"),
        icon: "error",
        customClass: {
          container: "my-swal-container",
        }
      });
    }

  }

  useEffect(() => {
    if(drawerConfig.isOpen){

      useHookForm.reset({
        salaryValue: drawerConfig.data.salaryValue || "",
        start: drawerConfig.data.start || null,
        end: drawerConfig.data.end || null,
        remark: drawerConfig.data.remark || "",
      })

    }
  }, [drawerConfig.isOpen])

  return (
    <DrawerCustom
      title={`${t("Edit")} ${t("Salary")}`}
      anchor={"right"}
      open={drawerConfig.isOpen}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: "500px",
        },
      }}
    >
      <StyledRoot>
        <form onSubmit={useHookForm.handleSubmit(onSubmit)}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography color="text.third" fontSize="14px" fontWeight="600" marginBottom="8px">{t("EmployeeID")}</Typography>
                <Controller
                  name="salaryValue"
                  control={useHookForm.control}
                  render={({field, fieldState}) => (
                    <TextFieldTheme
                      {...field}
                      InputProps={{
                        inputComponent: NumberFormatTheme,
                      }}
                      inputProps={{
                        allowNegative: false,
                        allowLeadingZeros: false,
                        onValueChange: (values) => {
                          const { value } = values;
                          field.onChange(value);
                        },
                        thousandSeparator: ",",
                        decimalScale: 2,
                        fixedDecimalScale: true,
                        value: field.value,
                      }}
                      onChange={() => {}}
                      onBlur={field.onBlur}
                      error={fieldState.error? true: false}
                      helperText={fieldState.error? fieldState.error.message: null}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography color="text.third" fontSize="14px" fontWeight="600" marginBottom="8px">{t("StartDate")}</Typography>
                <Controller
                  name="start"
                  control={useHookForm.control}
                  render={({ field, fieldState }) => (
                    <DatePicker
                      {...field}
                      views={["year", "month", "day"]}
                      inputFormat="DD MMMM YYYY"
                      openTo="year"
                      value={field.value}
                      onChange={(newValue) => {
                        field.onChange(newValue);
                      }}
                      renderInput={(params) => (
                        <TextFieldTheme
                          {...params}
                          inputProps={{
                            ...params.inputProps,
                            placeholder: t("SelectDate"),
                            readOnly: true
                          }}
                          onBlur={field.onBlur}
                          error={fieldState.error? true : false}
                          helperText={fieldState.error? fieldState.error.message : null}
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography color="text.third" fontSize="14px" fontWeight="600" marginBottom="8px">{t("Reason")}</Typography>
                <Controller
                  name="remark"
                  control={useHookForm.control}
                  render={({ field, fieldState }) => (
                    <TextFieldTheme
                      {...field}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            style={{
                              alignSelf: "flex-end",
                              fontSize: "12px",
                              userSelect: "none",
                            }}
                          >
                            {500 - field.value.length}
                          </InputAdornment>
                        ),
                      }}
                      multiline
                      minRows={3}
                      placeholder={`${t("Reason")}`}
                      error={fieldState.error? true: false}
                      helperText={fieldState.error? fieldState.error.message: null}
                      onChange={(event) => {
                        if (event.target.value.length > 500) {
                          field.onChange(event.target.value.slice(0, 500));
                        } else {
                          field.onChange(event.target.value);
                        }
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} container justifyContent="space-between">
                <ButtonBlue variant="text" onClick={handleClose} disabled={useHookForm.formState.isSubmitting}>{t("Cancel")}</ButtonBlue>
                <ButtonBlue variant="contained" type="submit" disabled={useHookForm.formState.isSubmitting}>{t("Save")}</ButtonBlue>
              </Grid>
            </Grid>
          </LocalizationProvider>
        </form>
      </StyledRoot>
    </DrawerCustom>
  )
}

export default DrawerEditSalaryChange;